import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { LEGAL_HOLD_REASON_MAX, legalHoldCreateSchema } from '@today/models'
import type { FormState, LegalHoldFormValues, LegalHoldPanelViewProps, LegalHoldRowView } from '@today/ui-domain'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { complianceCapabilities } from '@/auth/compliance-capabilities'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { COMPLIANCE_NAMESPACE } from '@/i18n'
import { complianceService } from '@/services/compliance.service'
import { complianceError } from './compliance-errors'
import { formatDate } from './compliance-ui'
import { ERASURES_KEY } from './useErasureList'

const HOLDS_KEY = ['compliance', 'legal-holds'] as const
/** Active holds are few; one page of the API's maximum covers them. */
const ACTIVE_HOLDS = { page: '1', limit: '100', 'filters[active]': 'true' }
const EMPTY: LegalHoldFormValues = { customerId: '', reason: '' }
type Errors = Partial<Record<keyof LegalHoldFormValues, string>>

const validate = (values: LegalHoldFormValues, t: (key: string) => string): Errors => {
	const errors: Errors = {}
	if (!legalHoldCreateSchema.shape.customerId.safeParse(values.customerId.trim()).success) {
		errors.customerId = t('validation.customerId')
	}
	const reason = values.reason.trim()
	if (!reason) errors.reason = t('validation.reason')
	else if (reason.length > LEGAL_HOLD_REASON_MAX) errors.reason = t('validation.reasonLength')
	return errors
}

/** A hold changes erasure statuses too, so both lists refresh after a place or release. */
function useRefreshHolds() {
	const queryClient = useQueryClient()
	return () => {
		void queryClient.invalidateQueries({ queryKey: HOLDS_KEY })
		void queryClient.invalidateQueries({ queryKey: ERASURES_KEY })
	}
}

/** The "Place legal hold" form state (REQ-204): local checks, then the API; a refusal keeps the values. */
function usePlaceHoldForm(): FormState<LegalHoldFormValues> {
	const { t } = useTranslation(COMPLIANCE_NAMESPACE)
	const refresh = useRefreshHolds()
	const [values, setValues] = useState(EMPTY)
	const [errors, setErrors] = useState<Errors>({})
	const [message, setMessage] = useState<string | null>(null)
	const place = useMutation({
		mutationFn: complianceService.placeLegalHold,
		onSuccess: () => {
			setValues(EMPTY)
			refresh()
		},
		onError: (error) => {
			const view = complianceError(error, t)
			setMessage(view.message)
			setErrors(view.fields)
		},
	})
	return {
		values,
		errors,
		message,
		submitting: place.isPending,
		onChange: (field, value) => setValues((current) => ({ ...current, [field]: value })),
		onSubmit: () => {
			const found = validate(values, t)
			setErrors(found)
			setMessage(null)
			if (Object.keys(found).length === 0) {
				place.mutate({ customerId: values.customerId.trim(), reason: values.reason.trim() })
			}
		},
	}
}

/**
 * The Legal holds panel (TO-232 compliance-admin-ui REQ-204): active holds, the
 * "Place legal hold" form and a release that asks first — the form and release
 * only with `compliance.legal_hold.manage`.
 */
export function useLegalHolds(): LegalHoldPanelViewProps {
	const { t, i18n } = useTranslation(COMPLIANCE_NAMESPACE)
	const caps = complianceCapabilities(useViewerPermissions().permissions)
	const refresh = useRefreshHolds()
	const form = usePlaceHoldForm()
	const [message, setMessage] = useState<string | null>(null)
	const [releaseTarget, setReleaseTarget] = useState<LegalHoldRowView | null>(null)
	const list = useQuery({
		queryKey: [...HOLDS_KEY, ACTIVE_HOLDS],
		queryFn: () => complianceService.listLegalHolds(ACTIVE_HOLDS),
	})
	const release = useMutation({
		mutationFn: complianceService.releaseLegalHold,
		onSuccess: refresh,
		onError: (error) => setMessage(complianceError(error, t).message),
	})
	const rows = (list.data?.data ?? []).map((hold) => ({
		id: hold.id,
		customerId: hold.customerId,
		reason: hold.reason,
		placedAt: formatDate(hold.placedAt, i18n.language),
	}))
	return {
		t,
		rows,
		error: list.isError,
		message,
		form: caps.manageHolds ? form : undefined,
		onRelease: caps.manageHolds ? (id) => setReleaseTarget(rows.find((row) => row.id === id) ?? null) : undefined,
		releaseTarget,
		onReleaseAccept: () => {
			if (releaseTarget) {
				setMessage(null)
				release.mutate(releaseTarget.id)
			}
			setReleaseTarget(null)
		},
		onReleaseDismiss: () => setReleaseTarget(null),
		onRetry: () => void list.refetch(),
	}
}
